import { QueryClient, QueryFilters } from "@tanstack/react-query";
import {
  ParamsForMethod,
  QueryDataForMethod,
  QueryMethodForModel,
  ModelName,
  TellescopeQueryKey,
  TellescopeQueryUpdate,
} from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { getTellescopeQueriesData } from "./get-tellescope-queries-data";

/**
 * Arguments for retrieving all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 */
export interface GetTellescopeModelQueriesDataArgs<Model extends ModelName, TError = Error> {
  /** The Tellescope model to retrieve queries for */
  model: Model;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /**
   * Optional filters applied to all queries for this model.
   * These filters are combined with method-specific filters if both are provided.
   */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TError,
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TellescopeQueryKey<Model, QueryMethodForModel<Model>>
    >,
    "queryKey"
  >;
  /**
   * Optional method-specific filters. Allows filtering queries for specific methods
   * and optionally matching by request parameters.
   */
  methodFilters?: Partial<{
    [Method in QueryMethodForModel<Model>]: Omit<
      TellescopeQueryFilters<
        QueryDataForMethod<Model, Method>,
        TError,
        QueryDataForMethod<Model, Method>,
        TellescopeQueryKey<Model, Method>
      >,
      "queryKey"
    > & {
      /** Optional request parameters to match for this method */
      request?: ParamsForMethod<Model, Method>;
      requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
    };
  }>;
}

/**
 * Return type for retrieving all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name
 */
export interface GetTellescopeModelQueriesDataReturn<Model extends ModelName> {
  /**
   * Queries grouped by method name. Each method contains an array of
   * [queryKey, data] tuples for all queries using that method.
   */
  queriesByMethod: Partial<{
    [Method in QueryMethodForModel<Model>]: TellescopeQueryUpdate<Model, Method>[];
  }>;
  /**
   * All queries for this model as a flat array of [queryKey, data] tuples,
   * regardless of method.
   */
  allQueries: TellescopeQueryUpdate<Model>[];
}

/**
 * Retrieves all cached queries for a specific Tellescope model, organized by method.
 *
 * This function provides a comprehensive view of all cached data for a model across
 * all query methods. It returns both a flat list of all queries and a grouped view
 * organized by method name.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 *
 * @param args - Configuration object containing model, queryClient, and optional filters
 * @returns An object containing `allQueries` (flat array) and `queriesByMethod` (grouped by method)
 *
 * @example
 * ```ts
 * // Get all user queries
 * const { allQueries, queriesByMethod } = getTellescopeModelQueriesData({
 *   queryClient,
 *   model: "users",
 * });
 *
 * // Access queries by method
 * const getOneQueries = queriesByMethod.getOne; // Array of [queryKey, data] tuples
 * const getSomeQueries = queriesByMethod.getSome;
 * ```
 *
 * @example
 * ```ts
 * // Use method-specific filters
 * const result = getTellescopeModelQueriesData({
 *   queryClient,
 *   model: "users",
 *   methodFilters: {
 *     getOne: {
 *       request: { id: "user-123" }, // Only get queries with this request
 *       stale: true, // Only include stale queries
 *     },
 *   },
 * });
 * ```
 */
export const getTellescopeModelQueriesData = <Model extends ModelName, TError = Error>({
  model,
  queryClient,
  filters,
  methodFilters,
}: GetTellescopeModelQueriesDataArgs<Model, TError>): GetTellescopeModelQueriesDataReturn<Model> => {
  const allQueries = queryClient.getQueriesData({
    queryKey: [model],
    exact: false,
    ...filters,
  } as QueryFilters) as TellescopeQueryUpdate<Model>[]; // Tanstack doesn't expose a generic for query key

  const methodNames = allQueries.reduce<Set<QueryMethodForModel<Model>>>((acc, [[_model, method]]) => {
    acc.add(method as QueryMethodForModel<Model>);
    return acc;
  }, new Set<QueryMethodForModel<Model>>());

  const queriesByMethod = Array.from(methodNames).reduce<GetTellescopeModelQueriesDataReturn<Model>["queriesByMethod"]>(
    (acc, method) => {
      const { request, requestKeysToOmitFromQueryKey, ...methodSpecificFilters } = methodFilters?.[method] ?? {};
      const methodQueries = getTellescopeQueriesData({
        model,
        method,
        request,
        queryClient,
        filters: methodSpecificFilters,
        requestKeysToOmitFromQueryKey,
      });
      acc[method] = methodQueries;
      return acc;
    },
    {}
  );

  return {
    allQueries,
    queriesByMethod,
  };
};
